<template>
  <VaSidebar v-model="writableVisible" :width="sidebarWidth" :color="color" minimized-width="0">
    <VaInput v-model="filter" :placeholder="t('general.search')" class="w-full" clearable>
      <template #prependInner>
        <VaIconMagnifier />
      </template>
    </VaInput>
    <VaAccordion v-show="favoriteMenuItem.children?.length" v-model="valueFavorite" multiple>
      <VaCollapse :key="favoriteMenuItem.id * -1">
        <template #header="{ value: isCollapsed }">
          <VaSidebarItem
            :to="favoriteMenuItem.children ? undefined : { name: favoriteMenuItem.route }"
            :aria-label="`${favoriteMenuItem.children ? 'Open category ' : 'Visit'} ${t(
              favoriteMenuItem.displayNameKey as string,
            )}`"
            role="button"
            hover-opacity="0.10"
          >
            <VaSidebarItemContent class="py-1 pr-2 pl-4">
              <VaIcon
                v-if="favoriteMenuItem.icon"
                aria-hidden="true"
                :name="favoriteMenuItem.icon"
                size="20px"
                color="secondary"
              />
              <VaSidebarItemTitle class="flex justify-between items-center leading-5 font-semibold" color="secondary">
                {{ t(favoriteMenuItem.displayNameKey as string) }}
                <VaIcon v-if="favoriteMenuItem.children" :name="arrowDirection(isCollapsed)" size="20px" />
              </VaSidebarItemTitle>
            </VaSidebarItemContent>
          </VaSidebarItem>
        </template>
        <template #body>
          <div v-for="childRoute in favoriteMenuItem.children" :key="childRoute.id">
            <VaSidebarItem
              :to="{ name: childRoute.route, params: childRoute.routeParam ? JSON.parse(childRoute.routeParam) : {} }"
              :aria-label="`Visit ${t(childRoute.displayNameKey as string)}`"
              hover-opacity="0.10"
            >
              <VaSidebarItemContent class="py-1 pr-2 pl-8">
                <VaIcon
                  v-if="childRoute.icon"
                  aria-hidden="true"
                  :name="childRoute.icon"
                  size="20px"
                  color="secondary"
                />
                <VaSidebarItemTitle class="leading-5 font-semibold">
                  {{ childRoute.displayName ?? t(childRoute.displayNameKey as string) }}
                </VaSidebarItemTitle>
                <VaIcon
                  v-if="childRoute.route"
                  aria-hidden="true"
                  name="favorite"
                  size="20px"
                  :class="{ 'material-symbols-outlined': !childRoute.isFavorite }"
                  color="secondary"
                  @click="handleFavorite(childRoute)"
                />
              </VaSidebarItemContent>
            </VaSidebarItem>
          </div>
        </template>
      </VaCollapse>
    </VaAccordion>
    <SidebarMenuItem
      v-for="routeItem in menuHierarchyDisplay"
      :key="routeItem.id"
      :item="routeItem"
      :active-color="activeColor"
      :icon-color="iconColor"
      :text-color="textColor"
      :arrow-direction="arrowDirection"
      :handle-favorite="handleFavorite"
      :level="0"
      :force-expand="forceExpand"
    />
  </VaSidebar>
</template>

<script setup lang="ts">
import { watch, ref, computed, onMounted } from "vue"
import { useRoute, useRouter } from "vue-router"
import { useCommon } from "@/pages/common/composables/useCommon"
import { useReport } from "@/pages/report/composables/useReport"
import { useGlobalStore } from "@/stores/global-store"
import { useUserStore } from "@/stores/user-store"
import { useI18n } from "vue-i18n"
import { useColors } from "vuestic-ui"
import { canAccessRoute } from "@/services/auth"
import { MenuHierarchy } from "@/pages/admin/menuHierarchy/types"
import { storeToRefs } from "pinia"
import SidebarMenuItem from "./components/SidebarMenuItem.vue"
import VaIconMagnifier from "@shared/components/icons/VaIconMagnifier.vue"
import { canAccess } from "@/services/authorizationPlugin"
import { PermissionAction } from "@shared/components/core/helpers/securityTypes"

// Props
interface Props {
  visible?: boolean
  mobile?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  visible: true,
  mobile: false,
})

// Emits
const emit = defineEmits<{
  "update:visible": [value: boolean]
}>()

// Composables
const { getColor, colorToRgba } = useColors()
const route = useRoute()
const router = useRouter()
const { t } = useI18n()
const globalStore = useGlobalStore()
const userStore = useUserStore()
const { userPreferences } = storeToRefs(globalStore)
const { user } = storeToRefs(userStore)
const { getSettings, saveSettings, getMenuHierarchy } = useCommon()
const { getReports } = useReport()

// Reactive data
const allRoutes = router.getRoutes()
const favoriteMenuItem = ref<MenuHierarchy>({
  id: -1,
  displayNameKey: "general.favorites",
  icon: "favorite",
  isActive: false,
  isFavorite: false,
  children: [],
  displayOrder: -1,
})
const reportsMenuItem = ref<MenuHierarchy>({
  id: -2,
  displayNameKey: "general.reports",
  icon: "assignment",
  isActive: true,
  isFavorite: false,
  children: [],
  displayOrder: 9999,
})
let rawMenuHierarchy: MenuHierarchy[] = []
const menuHierarchy = ref<MenuHierarchy[]>([])
const menuHierarchyDisplay = ref<MenuHierarchy[]>([])
const filter = ref<string>("")
const forceExpand = ref(false)
const value = ref<boolean[]>([])
const valueFavorite = ref<boolean[]>([])

// Computed
const writableVisible = computed({
  get: () => props.visible,
  set: (v: boolean) => emit("update:visible", v),
})

const sidebarWidth = computed(() => (props.mobile ? "100vw" : "350px"))
const color = computed(() => getColor("background-secondary"))
const activeColor = computed(() => colorToRgba(getColor("focus"), 0.3))

const routeHasActiveChild = (section: MenuHierarchy): boolean => {
  if (!section.children && route.name) {
    return isActiveRoute(section)
  } else if (section.children && route.name) {
    return section.children.some((item) => isActiveRoute(item))
  }
  return false
}

const isActiveRoute = (child: MenuHierarchy): boolean => {
  if (child.route === "report-viewer") {
    const paramJson = child.routeParam ? JSON.parse(child.routeParam) : {}
    return route.params.id == paramJson.id
  }
  return route!.name!.toString().endsWith(`${child.route}`)
}

const setActiveExpand = () => {
  value.value = menuHierarchy.value.map((route: MenuHierarchy) => routeHasActiveChild(route))!
}

const iconColor = (route: MenuHierarchy) => (routeHasActiveChild(route) ? "primary" : "secondary")
const textColor = (route: MenuHierarchy) => (routeHasActiveChild(route) ? "primary" : "textPrimary")
const arrowDirection = (state: boolean) => (state ? "va-arrow-up" : "va-arrow-down")

async function loadMenu() {
  rawMenuHierarchy = await getMenuHierarchy()

  handleMenuPermissions()
  handleMenuHierarchy(null)
  handleFavoriteMenu()
  handleReportsMenu()
  setActiveExpand()
}

function handleMenuPermissions() {
  const rawMenu = [...rawMenuHierarchy]
  rawMenu.forEach((item) => {
    if (!item.isActive) return
    const route = allRoutes.find((r) => r.name === item.route)
    if (item.route && (!route || !canAccessRoute(route.name as string))) {
      item.isActive = false
    }
  })
  menuHierarchy.value = rawMenu

  // Check if user has access to the current route. If not, redirect to 404
  if (route.name && !canAccessRoute(route.name.toString())) {
    router.push({ name: "401" })
  }
}

function handleMenuHierarchy(parent: MenuHierarchy | null) {
  let parents: MenuHierarchy[] | null = null
  const favoriteIds = userPreferences.value.menuFavorites ? userPreferences.value.menuFavorites.split(",") : []
  menuHierarchy.value.forEach((item) => {
    item.isFavorite = favoriteIds.includes(item.id.toString())
  })

  if (!parent) {
    parents = menuHierarchy.value
      .filter((item) => !item.parentId && item.isActive)
      .sort((a, b) => a.displayOrder - b.displayOrder)
  } else {
    parents = menuHierarchy.value
      .filter((item) => item.parentId === parent.id && item.isActive)
      .sort((a, b) => a.displayOrder - b.displayOrder)
    parent.children = parents.length ? parents : null
  }

  for (const parentItem of parents) {
    handleMenuHierarchy(parentItem)
  }

  if (!parent) {
    if (filter.value) {
      for (const item of parents) {
        const filteredMenu = filterMenu(item)
        if (!filteredMenu) {
          parents = parents.filter((p) => p.id !== item.id)
        }
      }
    }

    menuHierarchyDisplay.value = parents.filter((item) => item.isActive).sort((a, b) => a.displayOrder - b.displayOrder)

    if (filter.value) {
      forceExpand.value = true
    }
  }
}

function filterMenu(item: MenuHierarchy): MenuHierarchy | null {
  if (item.children) {
    for (const child of item.children) {
      const filteredMenu = filterMenu(child)
      if (!filteredMenu) {
        item.children = item.children?.filter((c) => c.id !== child.id)
      }
    }
    if (item.children.length) {
      return item
    }
  }

  if (
    t(item.displayNameKey as string)
      .toLowerCase()
      .includes(filter.value.toLowerCase())
  ) {
    return item
  }

  return null
}

function handleFavorite(route: MenuHierarchy) {
  route.isFavorite = !route.isFavorite

  const allFavoritesIds = menuHierarchy.value
    .filter((item) => item.isFavorite)
    .map((item) => item.id)
    .join(",")

  saveSettings("menuFavorites", allFavoritesIds)
  globalStore.setFavorites(allFavoritesIds)
}

function handleFavoriteMenu() {
  const favoriteIds = userPreferences.value.menuFavorites ? userPreferences.value.menuFavorites.split(",") : []
  const favoriteChildren: MenuHierarchy[] = []

  favoriteIds.forEach((id) => {
    const fav = menuHierarchy.value.find((item) => item.id.toString() === id && item.isActive)
    if (fav) {
      fav.isFavorite = true
      favoriteChildren.push(fav)
    }
  })

  favoriteMenuItem.value.children = favoriteChildren.length ? favoriteChildren : null
}

async function handleReportsMenu() {
  // get only items where the routeParam contains "ReportPath"
  const menuWithReports = menuHierarchy.value.filter(
    (item) => item.routeParam?.toLocaleLowerCase().includes("reportpath"),
  )
  if (!menuWithReports) return

  const reportList = await getReports()
  const rootMenu = reportList[0]

  if (!rootMenu || !rootMenu.children) return

  menuWithReports.forEach((element: MenuHierarchy) => {
    const routeParam = JSON.parse(element.routeParam as string)
    const reportMenuPath = routeParam.ReportPath as string
    const reports = rootMenu.children?.find((x: any) => x.displayName === reportMenuPath)

    if (reports && reports.children) {
      const newReportMenuItem = { ...reportsMenuItem.value }
      newReportMenuItem.children = []
      for (const report of reports.children) {
        if (report.securityResource && !canAccess(report.securityResource, PermissionAction.Read)) continue
        const newReportItem = {
          id: report.id * -1,
          displayName: report.displayName as string,
          displayNameKey: "",
          isActive: true,
          isFavorite: false,
          route: "report-viewer",
          routeParam: JSON.stringify({ id: report.id }),
          children: null,
          displayOrder: 0,
        }
        newReportMenuItem.children?.push(newReportItem)
        menuHierarchy.value.push(newReportItem)
      }

      if (newReportMenuItem.children?.length) {
        element.children?.push(newReportMenuItem)
      }
    }
  })

  handleFavoriteMenu()
}

// Watchers
watch(() => route.fullPath, setActiveExpand, { immediate: true })
watch(
  () => userPreferences.value.menuFavorites,
  () => {
    handleFavoriteMenu()
  },
  { immediate: true },
)
watch(
  () => filter.value,
  () => {
    handleMenuHierarchy(null)
    setActiveExpand()
  },
)
watch(
  () => user.value.userName,
  async () => {
    rawMenuHierarchy = await getMenuHierarchy()
    handleMenuPermissions()
    handleMenuHierarchy(null)
  },
)

// Lifecycle
onMounted(() => {
  getSettings()
  loadMenu()
})
</script>
